import type { JSX } from 'solid-js';

// Radial progress indicator — SVG, so a percentage lands on an exact stroke
// fraction rather than approximating a circle out of borders. First
// ring/radial primitive in the dashboard (no such shape exists yet, in this
// app or `@developerz/ui`); built for the "Custom plans" section's per-plan
// ring (#4341), reusable wherever a fraction wants a ring instead of
// `.progress`'s bar.
//
// THE STROKE IS INK, not the brand accent: `var(--primary)`, exactly like
// `.progress__indicator` (`styles/ui/_progress.scss`) already paints the
// linear bar. `working` (the accent) means LIVE state only
// (`scripts/accent-discipline.test.ts`, `docs/standards/design-system.md` §
// Accent) — a plan's percent-complete is a settled fact read from the last
// poll, not something happening on screen right now, so it never earns the
// accent. Do not repaint this ring in the working hue for any reason.
//
// `label` IS THE WHOLE ACCESSIBLE NAME, already translated by the caller —
// this component calls no `t()` of its own, so it can carry a "3 of 5 landed"
// or a "Cancelled" name without knowing which key produced it.
//
// The mount-in sweep is pure CSS (`progress-ring-fill` keyframe in
// `_progress.scss`), the same technique `.goal__progress .progress__indicator`
// uses (`views/_goals.scss`): no JS animation loop, so there is nothing here to
// desync from `prefers-reduced-motion` — the keyframe itself is gated.
//
// `pct` IS CLAMPED HERE, defensively: a caller's own division (merged /
// total) can hand back anything if a denominator upstream is ever wrong, and
// a ring is chrome that should never ask `stroke-dashoffset` for a fraction
// outside 0..1.
const DEFAULT_SIZE = 40;
const STROKE_WIDTH = 4;

function clampPct(pct: number): number {
  if (!Number.isFinite(pct)) return 0;
  return Math.min(100, Math.max(0, pct));
}

export function ProgressRing(props: { pct: number; label: string; size?: number }): JSX.Element {
  const size = (): number => props.size ?? DEFAULT_SIZE;
  const radius = (): number => (size() - STROKE_WIDTH) / 2;
  const center = (): number => size() / 2;
  const circumference = (): number => 2 * Math.PI * radius();
  const clamped = (): number => clampPct(props.pct);
  // fraction=0 -> offset=circumference (nothing drawn); fraction=1 -> offset=0
  // (the full circle drawn). This is also the "empty" value the CSS mount
  // animation reads (`--progress-ring-empty`), so a 0% ring renders identical
  // to its own animated-out state and never visibly "sweeps" from nothing.
  const offset = (): number => circumference() * (1 - clamped() / 100);

  return (
    <svg
      class="progress-ring"
      role="img"
      aria-label={props.label}
      width={size()}
      height={size()}
      viewBox={`0 0 ${size()} ${size()}`}
    >
      <circle
        class="progress-ring__track"
        cx={center()}
        cy={center()}
        r={radius()}
        stroke-width={STROKE_WIDTH}
        fill="none"
      />
      <circle
        class="progress-ring__indicator"
        style={{ '--progress-ring-empty': `${circumference()}px` }}
        cx={center()}
        cy={center()}
        r={radius()}
        stroke-width={STROKE_WIDTH}
        fill="none"
        stroke-dasharray={String(circumference())}
        stroke-dashoffset={String(offset())}
        transform={`rotate(-90 ${center()} ${center()})`}
      />
      {/* Decorative: the svg's own `role="img"` + `aria-label` above is the
          whole accessible name and, per the ARIA-in-HTML mapping, presentational
          children of a `role="img"` are pruned from the accessibility tree, so
          this text does not risk a screen reader announcing the percentage
          twice. (`aria-hidden` here would be redundant AND is refused on a
          `<text>` node by `lint/a11y/noAriaHiddenOnFocusable`.) */}
      <text
        class="progress-ring__value"
        x="50%"
        y="50%"
        text-anchor="middle"
        dominant-baseline="central"
      >
        {Math.round(clamped())}%
      </text>
    </svg>
  );
}
